import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { RequestPageQuery } from '../generation/generationApi';

/** Matches the backend's `DEFAULT_ADMIN_LIST_LIMIT`. */
export const ADMIN_PAGE_SIZE = 20;

export interface PagedListResult<TItem> {
  items: TItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  /** Re-runs the page that failed, keeping everything already loaded. */
  retry: () => void;
  /** Swaps one row for a newer copy of itself, e.g. a flag just closed. */
  replace: (item: TItem) => void;
  /** Drops one row that no longer belongs in this list. */
  remove: (id: string) => void;
}

interface Loaded<TItem> {
  offset: number;
  nonce: number;
  items: TItem[];
  hasMore: boolean;
  error: string | null;
}

/**
 * Offset paging for the operator lists, in the same shape as the customer-facing
 * `useRequestHistory`: a tagged result, an `AbortController` per page, and no
 * state written synchronously inside the effect.
 *
 * `fetchPage` must be stable (wrap it in `useCallback`), and a list whose
 * *filter* changes is a different list — remount it with a React `key` rather
 * than swapping the fetcher underneath it, so the offset resets with it.
 */
export function usePagedList<TItem>(
  fetchPage: (page: RequestPageQuery, signal: AbortSignal) => Promise<TItem[]>,
  idOf: (item: TItem) => string,
): PagedListResult<TItem> {
  const [loaded, setLoaded] = useState<Loaded<TItem> | null>(null);
  const [offset, setOffset] = useState(0);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const page = await fetchPage({ limit: ADMIN_PAGE_SIZE, offset }, controller.signal);
        if (cancelled) return;
        setLoaded((previous) => ({
          offset,
          nonce,
          items: previous !== null && offset > 0 ? append(previous.items, page, idOf) : page,
          hasMore: page.length === ADMIN_PAGE_SIZE,
          error: null,
        }));
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded((previous) => ({
          offset,
          nonce,
          items: previous?.items ?? [],
          hasMore: previous?.hasMore ?? false,
          error: getErrorMessage(caught, 'Could not load that list.'),
        }));
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchPage, idOf, offset, nonce]);

  const isCurrentPage = loaded !== null && loaded.offset === offset && loaded.nonce === nonce;
  const isFetching = !isCurrentPage;

  const loadMore = useCallback(() => {
    if (loaded === null || !isCurrentPage || !loaded.hasMore) return;
    setOffset((value) => value + ADMIN_PAGE_SIZE);
  }, [isCurrentPage, loaded]);

  const retry = useCallback(() => setNonce((value) => value + 1), []);

  const replace = useCallback(
    (item: TItem) => {
      setLoaded((previous) =>
        previous === null
          ? previous
          : {
              ...previous,
              items: previous.items.map((held) => (idOf(held) === idOf(item) ? item : held)),
            },
      );
    },
    [idOf],
  );

  const remove = useCallback(
    (id: string) => {
      setLoaded((previous) =>
        previous === null
          ? previous
          : { ...previous, items: previous.items.filter((held) => idOf(held) !== id) },
      );
    },
    [idOf],
  );

  return {
    items: loaded?.items ?? [],
    isLoading: isFetching && offset === 0,
    isLoadingMore: isFetching && offset > 0,
    error: isCurrentPage ? loaded.error : null,
    hasMore: isCurrentPage && loaded.hasMore,
    loadMore,
    retry,
    replace,
    remove,
  };
}

/** Appends a page, dropping ids already held. */
function append<TItem>(previous: TItem[], page: TItem[], idOf: (item: TItem) => string): TItem[] {
  const seen = new Set(previous.map(idOf));
  return [...previous, ...page.filter((item) => !seen.has(idOf(item)))];
}
